import React, {Component} from 'react';
import {Link} from 'react-router-dom';
import {connect} from 'react-redux';
import {Button, Dropdown} from 'antd';
import {LockIcon} from "../Access/common/LockIcon";
import {withRouter} from "react-router";
import {LockTooltip} from "../Access/common/LockTooltip";
import { addAccessToItems } from '../Access/helper/utils/addAccessToItems';
import TransactionActions from "bukku/redux/Transaction/actions";

/**
 * Data Format for items
 * - key: string
 * - label: string
 * - icon: React Component (optional)
 *
 * // URL
 * - url: string
 * - blank: boolean         ( to open url in a new tab )
 * - appendTokens: boolean  ( to attach token to url )
 *
 * // Action
 * - action: Function       ( callback when it is clicked )
 *
 * // Disabling
 * - disabled: boolean      ( label will be greyed out, unclickable )
 * - disabledTooltipText: string ( tooltip text when disabled )
 *
 * // Permission & Subscription locking
 * - isAction: boolean
 *      => when true, it will check the permission / subscription of the current route
 * - permission: PermissionKey or PermissionKey[] (optional)
 * - requiredLevel: PermissionV2 (default: PermissionV2.level2; used together with permission)
 * - subscription: SubscriptionScopeKey or SubscriptionScopeKey[] (optional)
 * - transactionType: TransactionType (optional)
 * NOTE: when url is provided, it will also be checked for Permission & Subscription
 *
 * Behaviour
 * - When no permission: item will be hidden.
 * - When not in subscription: item will be disabled and isLocked icon be added.
 *
 */
const {
    transactionTableListMenuClick,
    transactionTableAfterRowClick,
} = TransactionActions;

class ListMenu extends Component {
    static defaultProps = {
        items: [],
        type: 'default',
        fullWidth: false,
        disabled: false,
        loading: false,
    };

    getContextMenuItems = items => {
        const {
            transactionTableListMenuClick,
            transactionTableAfterRowClick,
        } = this.props;
        const newItems = [...items];
        newItems.shift();

        const _this = this;
        function iter(item) {
            const className = item.type === 'danger' ? 'item-danger' : '';

            if (item.submenu) {
                return (
                    {
                        key: item.key,
                        label: _this.renderItem(item),
                        disabled: item.disabled || item.isLocked,
                        onClick: () => {
                            if (item.action) {
                                item.action();
                                transactionTableListMenuClick(item.key);
                                transactionTableAfterRowClick();
                            }
                        },
                        className,
                        danger: item.danger,
                        children: item.submenu.map(iter),
                    }
                );
            }

            if (item.group) {
                return (
                    {
                        type: 'group',
                        key: item.key,
                        label: item.label,
                        disabled: item.disabled || item.isLocked,
                        onClick: () => {
                            if (item.action) {
                                item.action();
                                transactionTableListMenuClick(item.key);
                                transactionTableAfterRowClick();
                            }
                        },
                        className,
                        danger: item.danger,
                        children: item.group.map(iter),
                    }
                );
            }

            return (
                {
                    key: item.key,
                    label: _this.renderItem(item),
                    disabled: item.disabled || item.isLocked,
                    onClick: () => {
                        if (item.action) {
                            item.action();
                            transactionTableListMenuClick(item.key);
                            transactionTableAfterRowClick();
                        }
                    },
                    className,
                    danger: item.danger,
                }
            );
        }

        const menuItems = newItems.map(iter);
        return menuItems;
    };

    // getContextMenu = () => <Menu className="bukku-list-menu" items={this.getContextMenuItems()} />;

    renderLabel = item => {
        const icon = item.icon ? (
            <span>
                {item.icon}
                &nbsp;&nbsp;
            </span>
        ) : null;
        const lockedIcon = item.isLocked ? <LockIcon /> : null;
        const hint = item.hint && !item.isLocked ? <span className="key-hint">{item.hint}</span> : null;

        return (
            <span>
                { icon }
                { item.label }
                { hint }
                { lockedIcon }
            </span>
        );
    };

    renderItem = item => {
        const {
            accessToken,
            subdomain,
        } = this.props;

        let {url} = item;
        if (item.appendTokens)
            url += `${url.indexOf('?') === -1 ? '?' : '&'}token=${accessToken}&cs=${subdomain}`;

        let itemDiv;
        if (item.action) {
            itemDiv = this.renderLabel(item);
        }
        else if ((!item.blank || item.disabled) && url) {
            itemDiv = (
                <Link
                    disabled={item.disabled || item.isLocked}
                    to={url}
                >
                    { this.renderLabel(item) }
                </Link>
            );
        }
        else {
            itemDiv = <a href={url} target="_blank" rel="noopener noreferrer">{this.renderLabel(item)}</a>;
        }

        if (item.isLocked || (item.disabled && item.disabledTooltipText)) {
            return (
                <LockTooltip
                    placement="left"
                    title={item.disabledTooltipText || null}
                >
                    {this.renderLabel(item)}
                </LockTooltip>);
        }
        return itemDiv;
    };

    // Check for Permission & Subscription
    getItems = () => {
        const { items } = this.props;
        return addAccessToItems(items);
    };

    reorderItems = items => {
        const disabled = [];
        for (let idx = 0; idx < items.length; idx += 1) {
            const item = items[idx];
            if (item.disabled || item.isLocked)
                disabled.push(item);

            else if (disabled.length === 0)
                return items;
            else
                return [item, ...disabled, ...items.slice(idx + 1, items.length)];
        }
        return disabled;
    };

    render() {
        const { type, key, className, fullWidth, disabled, loading } = this.props;

        const items = this.getItems();
        const style = fullWidth ? {width: '100%'} : {};

        if (items.length === 0) // Nothing
            return null;
        if (items.length === 1) { // Just one button
            const item = items[0];
            const buttonStyle = item.isLocked ? {...style, pointerEvents: 'none'} : style;

            const buttonDiv = (
                <Button
                    key={key}
                    type={type || item.type}
                    disabled={disabled || item.disabled || item.isLocked}
                    onClick={item.action}
                    className={className}
                    loading={loading}
                    style={buttonStyle}
                >
                    { this.renderItem(item) }
                </Button>
            );

            if (item.isLocked)
                return <LockTooltip>{ buttonDiv }</LockTooltip>;
            return buttonDiv;
        }
        if (items.length > 1) {
            const reorderedItems = this.reorderItems(items);
            const item = reorderedItems[0];
            return (
                <Dropdown.Button
                    key={key}
                    type={type || item.type}
                    trigger={['click', 'hover']}
                    onClick={item.action} // onClick if action is present (eg open modal)
                    menu={{
                        disabled: loading || item.loading,
                        items: this.getContextMenuItems(reorderedItems),
                    }}
                    // overlay={this.getContextMenu(reorderedItems)} @deprecated
                    className={className}
                    disabled={disabled || item.disabled || item.isLocked}
                    loading={loading || item.loading}
                    style={style}
                >
                    { this.renderItem(item) }
                </Dropdown.Button>
            );
        }
        return null;
    }
}

export default withRouter(connect(state => ({
    accessToken: state.Auth.accessToken,
    subdomain: state.Auth.subdomain,
}), {
    transactionTableListMenuClick,
    transactionTableAfterRowClick,
})(ListMenu));
